import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  ChevronLeft,
  Folder,
  FileText,
  DollarSign,
  RefreshCw,
  Loader2,
  Coins,
  CheckCircle2,
  XCircle,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BottomNavigation from "@/components/BottomNavigation";
import { subscriptionsAPI } from "@/api/subscriptions";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ScreenHeader, MonoValue } from "@/components/redesign";

// Define interface for subscription status details
interface SubscriptionStatusDetails {
  user_id: string;
  plan: string;
  source: string | null;
  ends_at: string | null;
  grace_until: string | null;
  current_time: string | null;
  active_project_count: number;
  passive_project_count: number;
  active_project_limit: number;
  projects_locked: boolean;
  trial_active: boolean;
  has_access: boolean;
  // Additional calculated fields
  renewal_at?: string | null;
  start_date?: string | null;
  subscription_period_days?: number | null;
  days_remaining?: number | null;
  subscription_progress_percentage?: number | null;
}

const PlanDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionStatusDetails | null>(null);
  const { user } = useAuth();

  // Calculate subscription metrics based on start_date and ends_at
  const calculateSubscriptionMetrics = (details: SubscriptionStatusDetails): {
    daysRemaining: number;
    progressPercentage: number;
    totalDays: number;
    elapsedDays: number;
  } => {
    if (!details.ends_at || !details.start_date) {
      return {
        daysRemaining: 0,
        progressPercentage: 0,
        totalDays: 0,
        elapsedDays: 0
      };
    }

    const now = new Date();
    const startDate = new Date(details.start_date);
    const endDate = new Date(details.ends_at);

    // Calculate total subscription period in days
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate days remaining
    const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

    // Calculate elapsed days
    const elapsedDays = Math.max(0, Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));

    // Calculate progress percentage (how much of the subscription period has elapsed)
    const progressPercentage = totalDays > 0 ? Math.min(100, (elapsedDays / totalDays) * 100) : 0;

    return {
      daysRemaining,
      progressPercentage,
      totalDays,
      elapsedDays
    };
  };

  // Function to fetch detailed subscription status
  const fetchSubscriptionDetails = async () => {
    setIsRefreshing(true);
    try {
      // Get the data directly from the database
      const details = await subscriptionsAPI.getSubscriptionStatusDetails();

      // Calculate additional metrics if they're not already provided by the view
      if (details && details.ends_at) {
        const metrics = calculateSubscriptionMetrics(details);
        details.days_remaining = metrics.daysRemaining;
        details.subscription_progress_percentage = metrics.progressPercentage;
        details.subscription_period_days = metrics.totalDays;
      }

      setSubscriptionDetails(details);

    } catch (error) {

      toast.error(t('plan_details.subscription_info_error'), {
        description: t('plan_details.try_again_later')
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch subscription details on component mount
  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  // 2-tier model: only Premium is paid.
  const getPlanPrice = (plan: string): number => (plan === 'premium' ? 749 : 0);

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return t('plan_details.not_specified');
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Handle navigation back to settings
  const handleBackToSettings = () => {
    navigate('/settings');
  };

  // Handle upgrade plan
  const handleUpgradePlan = () => {
    navigate('/subscription');
  };

  // Detail row component
  const DetailRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
  }> = ({ icon, label, value }) => (
    <div className="flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0 text-gray-400">{icon}</span>
        <span className="text-[14px] text-gray-700 break-words">{label}</span>
      </div>
      <span className="text-[14px] font-semibold text-gray-900 sm:ml-3 sm:text-right break-words">{value}</span>
    </div>
  );

  return (
    <div className="page-container bg-gray-50">
      <ScreenHeader
        title={t('plan_details.title')}
        showBack={true}
        onBack={handleBackToSettings}
        right={
          <button
            type="button"
            onClick={fetchSubscriptionDetails}
            disabled={isRefreshing}
            className="p-1.5 rounded-md text-brand-25 hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      <main className="main-content">
        <div className="px-4 pb-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
          ) : (
            <>
              {/* Current Plan Overview */}
              <div className={`rounded-2xl p-5 mb-4 border shadow-sm ${
                subscriptionDetails?.has_access
                  ? 'bg-brand-50 border-brand-200'
                  : 'bg-error-50 border-error-100'
              }`}>
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-[15px] font-bold text-gray-900">{t('plan_details.current_plan')}</span>
                  <span className={`text-[11px] font-bold px-3 py-1 rounded-full ${
                    subscriptionDetails?.has_access
                      ? 'bg-brand-100 text-brand-700'
                      : 'bg-error-100 text-error-600'
                  }`}>
                    {subscriptionDetails?.plan === 'premium' ? t('plan_details.premium') :
                     subscriptionDetails?.plan === 'elite' ? t('plan_details.elite') :
                     subscriptionDetails?.has_access ? t('plan_details.price_free') : t('plan_details.expired')}
                  </span>
                </div>

                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-success-600" />
                    <div className="flex items-baseline gap-1">
                      {getPlanPrice(subscriptionDetails?.plan || '') === 0 ? (
                        <span className="text-[18px] font-bold text-gray-900">{t('plan_details.price_free')}</span>
                      ) : (
                        <MonoValue
                          value={getPlanPrice(subscriptionDetails?.plan || '')}
                          suffix="₺"
                          format
                          size="md"
                          tone="brand"
                        />
                      )}
                      {getPlanPrice(subscriptionDetails?.plan || '') > 0 && (
                        <span className="text-[12px] text-gray-500">{t('plan_details.per_month')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {subscriptionDetails?.has_access ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-success-600" />
                        <span className="text-[13px] font-semibold text-success-600">{t('plan_details.active')}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-error-600" />
                        <span className="text-[13px] font-semibold text-error-600">{t('plan_details.inactive')}</span>
                      </>
                    )}
                  </div>
                </div>

                {subscriptionDetails?.has_access && subscriptionDetails?.plan !== 'premium' && (
                  <p className="text-[12px] font-medium text-success-600 mb-2">{t('plan_details.free_forever_note')}</p>
                )}

                {subscriptionDetails?.days_remaining && subscriptionDetails.days_remaining > 0 && (
                  <div className="space-y-1.5 mt-2">
                    <div className="flex justify-between text-[11px] text-gray-500">
                      <span className="font-semibold uppercase tracking-[0.04em]">{t('plan_details.subscription_progress')}</span>
                      <MonoValue
                        value={(subscriptionDetails?.days_remaining || 0)}
                        suffix={` ${t('settings.days_remaining', { defaultValue: 'gün kaldı' })}`}
                        size="meta"
                        tone={
                          (subscriptionDetails?.days_remaining || 0) <= 3 ? 'error' :
                          (subscriptionDetails?.days_remaining || 0) <= 7 ? 'muted' : 'muted'
                        }
                      />
                    </div>
                    <div className={`relative h-2 w-full overflow-hidden rounded-full ${
                      (subscriptionDetails?.days_remaining || 0) <= 3
                        ? 'bg-error-100'
                        : (subscriptionDetails?.days_remaining || 0) <= 7
                          ? 'bg-warning-50'
                          : 'bg-brand-100'
                    }`}>
                      <div
                        className={`h-full transition-all rounded-full ${
                          (subscriptionDetails?.days_remaining || 0) <= 3
                            ? 'bg-error-600'
                            : (subscriptionDetails?.days_remaining || 0) <= 7
                              ? 'bg-warning-600'
                              : 'bg-brand-600'
                        }`}
                        style={{ width: `${subscriptionDetails?.subscription_progress_percentage ?? 0}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription Details */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mb-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500 px-4 pt-4 pb-2">
                  {t('plan_details.subscription_details')}
                </p>

                <div className="divide-y divide-gray-200">
                  {/* Subscription Source */}
                  <DetailRow
                    icon={<Coins className="h-4 w-4 text-success-600" />}
                    label={t('plan_details.payment_source')}
                    value={
                      subscriptionDetails?.source === 'revenuecat' ? t('plan_details.app_store_google_play') :
                      subscriptionDetails?.source === 'stripe' ? t('plan_details.credit_card') :
                      t('plan_details.not_specified')
                    }
                  />

                  {/* Project Usage */}
                  <DetailRow
                    icon={<Folder className="h-4 w-4 text-brand-500" />}
                    label={t('plan_details.project_usage')}
                    value={
                      <MonoValue
                        value={`${subscriptionDetails?.active_project_count || 0}/${subscriptionDetails?.active_project_limit || 0}`}
                        size="sm"
                        tone="default"
                      />
                    }
                  />

                  {/* Passive Projects */}
                  <DetailRow
                    icon={<FileText className="h-4 w-4 text-gray-400" />}
                    label={t('plan_details.passive_projects')}
                    value={
                      <MonoValue
                        value={subscriptionDetails?.passive_project_count || 0}
                        size="sm"
                        tone="default"
                      />
                    }
                  />

                  {/* Start Date */}
                  <DetailRow
                    icon={<Calendar className="h-4 w-4 text-brand-400" />}
                    label={t('plan_details.start_date')}
                    value={
                      <MonoValue
                        value={formatDate(subscriptionDetails?.start_date || null)}
                        size="sm"
                        tone="default"
                      />
                    }
                  />

                  {/* End Date */}
                  <DetailRow
                    icon={<Calendar className="h-4 w-4 text-error-600" />}
                    label={t('plan_details.end_date')}
                    value={
                      subscriptionDetails?.ends_at ? (
                        <MonoValue
                          value={formatDate(subscriptionDetails.ends_at)}
                          size="sm"
                          tone="default"
                        />
                      ) : (
                        <span className="text-[13px] font-semibold text-success-600">{t('plan_details.unlimited')}</span>
                      )
                    }
                  />

                  {/* Renewal Date (if available) */}
                  {subscriptionDetails?.renewal_at && (
                    <DetailRow
                      icon={<DollarSign className="h-4 w-4 text-success-600" />}
                      label={t('plan_details.renewal_date')}
                      value={
                        <MonoValue
                          value={formatDate(subscriptionDetails?.renewal_at)}
                          size="sm"
                          tone="default"
                        />
                      }
                    />
                  )}

                  {/* Grace Period (if available) */}
                  {subscriptionDetails?.grace_until && (
                    <DetailRow
                      icon={<Calendar className="h-4 w-4 text-warning-600" />}
                      label={t('plan_details.grace_period')}
                      value={
                        <MonoValue
                          value={formatDate(subscriptionDetails?.grace_until)}
                          size="sm"
                          tone="default"
                        />
                      }
                    />
                  )}

                  {/* Projects Locked Status */}
                  <DetailRow
                    icon={<Folder className="h-4 w-4 text-warning-600" />}
                    label={t('plan_details.projects_locked')}
                    value={
                      <span className={`text-[13px] font-semibold ${subscriptionDetails?.projects_locked ? 'text-error-600' : 'text-success-600'}`}>
                        {subscriptionDetails?.projects_locked ? t('plan_details.yes') : t('plan_details.no')}
                      </span>
                    }
                  />
                </div>
              </div>

              {/* Plan Action Buttons */}
              <Button
                variant={!subscriptionDetails?.has_access ? "default" : "outline"}
                className={`w-full rounded-xl h-12 font-semibold ${
                  !subscriptionDetails?.has_access
                    ? 'bg-brand-600 hover:bg-brand-700 text-white'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                onClick={handleUpgradePlan}
              >
                {!subscriptionDetails?.has_access ? t('plan_details.upgrade_plan') : t('plan_details.change_plan')}
              </Button>
            </>
          )}
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default PlanDetailsPage;
